import { supabase } from '../lib/supabaseClient';
import type { Voice, UpdateVoiceModerationInput } from '../types/voices';

// 定数: トップページの新着表示件数
export const RECENT_VOICES_LIMIT = 5;

/**
 * 承認済みの最新の悩み投稿を取得
 */
export async function fetchRecentVoices(limit = RECENT_VOICES_LIMIT): Promise<Voice[]> {
  const { data, error } = await supabase
    .from('voices')
    .select('*')
    .eq('status', 'approved')
    .eq('is_published', true) // Phase53: 公開フラグ
    .order('updated_at', { ascending: false, nullsFirst: false })
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('Error fetching recent voices:', error);
    throw error;
  }

  return data || [];
}

/**
 * 承認済みの悩み投稿全件を取得(フィルタリング対応)
 */
export async function fetchVoices(filters?: {
  category?: string;
  grade?: string;
  keyword?: string;
}): Promise<Voice[]> {
  let query = supabase
    .from('voices')
    .select('*')
    .eq('status', 'approved')
    .eq('is_published', true); // Phase53: 公開フラグ

  // フィルタリング
  if (filters?.category) {
    query = query.eq('category', filters.category);
  }
  if (filters?.grade) {
    query = query.eq('grade', filters.grade);
  }

  query = query.order('is_featured', { ascending: false })
    .order('created_at', { ascending: false });

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching voices:', error);
    throw error;
  }

  let results = data || [];

  // キーワード検索(フロント側でフィルタ)
  if (filters?.keyword) {
    const keyword = filters.keyword.toLowerCase();
    results = results.filter((v) => {
      const titleMatch = v.title.toLowerCase().includes(keyword);
      const bodyMatch = (v.body_public || v.body_raw || '').toLowerCase().includes(keyword);
      return titleMatch || bodyMatch;
    });
  }

  return results;
}

/**
 * 管理者用：全ての悩み投稿を取得(ステータス問わず)
 */
export async function fetchAllVoices(): Promise<Voice[]> {
  const { data, error } = await supabase
    .from('voices')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching all voices:', error);
    throw error;
  }

  return data || [];
}

/**
 * IDまたはスラッグで悩み投稿1件を取得
 */
export async function fetchVoiceById(idOrSlug: string): Promise<Voice | null> {
  // まずスラッグで検索
  let { data, error } = await supabase
    .from('voices')
    .select('*')
    .eq('slug', idOrSlug)
    .single();

  // スラッグで見つからなければIDで検索
  if (error && error.code === 'PGRST116') {
    const result = await supabase
      .from('voices')
      .select('*')
      .eq('id', idOrSlug)
      .single();
    
    data = result.data;
    error = result.error;
  }

  if (error) {
    if (error.code === 'PGRST116') {
      return null;
    }
    console.error('Error fetching voice by id:', error);
    throw error;
  }

  return data;
}

/**
 * 似た悩みを取得(同じカテゴリ・近い学年)
 */
export async function fetchSimilarVoices(
  currentId: string,
  category?: string | null,
  grade?: string | null,
  limit = 3
): Promise<Voice[]> {
  let query = supabase
    .from('voices')
    .select('*')
    .eq('status', 'approved')
    .eq('is_published', true)
    .neq('id', currentId);

  if (category) {
    query = query.eq('category', category);
  }

  query = query.order('created_at', { ascending: false }).limit(limit);

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching similar voices:', error);
    return [];
  }

  return data || [];
}

/**
 * 新規悩み投稿を作成
 */
export async function createVoice(payload: {
  category: string;
  place_type: string;
  title: string;
  body_raw: string;
  email?: string;
}): Promise<Voice> {
  const { data, error } = await supabase
    .from('voices')
    .insert({
      category: payload.category,
      place_type: payload.place_type,
      title: payload.title,
      body_raw: payload.body_raw,
      body_public: payload.body_raw, // 同じ内容を保存
      email: payload.email || null,
      status: 'pending', // 承認待ち
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating voice:', error);
    throw error;
  }

  return data;
}

/**
 * 承認待ちの悩み投稿を取得(管理用)
 */
export async function fetchPendingVoices(limit = 50): Promise<Voice[]> {
  const { data, error } = await supabase
    .from('voices')
    .select('*')
    .eq('status', 'pending')
    .order('created_at', { ascending: true })
    .limit(limit);

  if (error) {
    console.error('Error fetching pending voices:', error);
    throw error;
  }

  return data || [];
}

/**
 * 悩み投稿の承認・編集(管理用)
 */
export async function updateVoiceModeration(
  id: string,
  input: UpdateVoiceModerationInput
): Promise<Voice> {
  const updateData: any = {
    updated_at: new Date().toISOString(),
  };

  // 渡されたフィールドだけを更新
  if (typeof input.status !== 'undefined') {
    updateData.status = input.status;
  }
  if (typeof input.is_published !== 'undefined') {
    updateData.is_published = input.is_published;
  }

  const { data, error } = await supabase
    .from('voices')
    .update(updateData)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * 管理者用：悩み投稿のステータスを更新
 */
export async function updateVoiceStatus(id: string, status: string): Promise<void> {
  const { error } = await supabase
    .from('voices')
    .update({ status, updated_at: new Date().toISOString() })
    .eq('id', id);

  if (error) {
    console.error('Error updating voice status:', error);
    throw error;
  }
}

/**
 * 管理者用：悩み投稿を削除
 */
export async function deleteVoice(id: string): Promise<void> {
  const { error } = await supabase
    .from('voices')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting voice:', error);
    throw error;
  }
}
